<script setup>
import { computed, ref } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { directive as vOnClickaway } from 'vue3-click-away';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Track which tracking dropdown is open (by index)
const openTrackingDropdown = ref(null);

const toggleTrackingDropdown = (index) => {
  openTrackingDropdown.value = openTrackingDropdown.value === index ? null : index;
};

const closeTrackingDropdown = () => {
  openTrackingDropdown.value = null;
};

// All available tracking services
const trackingServices = [
  { 
    id: 'parcelsapp', 
    name: 'Parcels App', 
    getUrl: (num) => `https://parcelsapp.com/en/tracking/${num}`,
    icon: 'i-lucide-package-search'
  },
  { 
    id: '17track', 
    name: '17track', 
    getUrl: (num) => `https://t.17track.net/en#nums=${num}`,
    icon: 'i-lucide-globe'
  },
  { 
    id: 'postnord', 
    name: 'PostNord', 
    getUrl: () => 'https://www.postnord.se/en/our-tools/track-and-trace/',
    icon: 'i-lucide-truck',
    manualEntry: true // Indicates user needs to manually enter tracking
  },
  { 
    id: 'posti', 
    name: 'Posti.fi', 
    getUrl: (num) => `https://www.posti.fi/en/tracking/${num}`,
    icon: 'i-lucide-package'
  },
  { 
    id: 'yunexpress', 
    name: 'Yun Express', 
    getUrl: (num) => `https://www.yuntrack.com/parcelTracking?id=${num}`,
    icon: 'i-lucide-plane'
  },
];

const formatDate = dateString => {
  return format(new Date(dateString), 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

const getStatusClass = status => {
  const classes = {
    paid: 'bg-n-teal-5 text-n-teal-12',
  };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const getStatusI18nKey = (type, status = '') => {
  return `CONVERSATION_SIDEBAR.SHOPIFY.${type.toUpperCase()}_STATUS.${status.toUpperCase()}`;
};

const fulfillmentStatus = computed(() => {
  const { fulfillment_status: status } = props.order;
  if (!status) {
    return 'Not Fulfilled';
  }
  return status === 'fulfilled' ? 'Fulfilled' : status === 'partial' ? 'Partially Fulfilled' : 'Not Fulfilled';
});

const financialStatus = computed(() => {
  const { financial_status: status } = props.order;
  if (!status) {
    return '';
  }
  return t(getStatusI18nKey('FINANCIAL', status));
});

const getFulfillmentClass = status => {
  const classes = {
    fulfilled: 'text-n-teal-9',
    partial: 'text-n-amber-9',
    unfulfilled: 'text-n-ruby-9',
  };
  return classes[status] || 'text-n-ruby-9';
};

const hasTracking = computed(() => {
  return props.order.tracking_info && props.order.tracking_info.length > 0;
});

const hasLineItems = computed(() => {
  return props.order.line_items && props.order.line_items.length > 0;
});

const shippingStatus = computed(() => {
  return props.order.shipping_status || 'Unknown';
});

const getShippingClass = status => {
  const classes = {
    Paid: 'text-n-amber-9',
    Free: 'text-n-teal-9',
  };
  return classes[status] || 'text-n-slate-11';
};

const getTrackingUrl = (trackingNumber, trackingUrl) => {
  // If tracking number starts with "JJ", use Posti.fi link
  if (trackingNumber && trackingNumber.startsWith('JJ')) {
    return `https://www.posti.fi/en/tracking/${trackingNumber}`;
  }
  // If tracking number starts with "YT", use Yun Express link
  if (trackingNumber && trackingNumber.startsWith('YT')) {
    return `https://www.yuntrack.com/parcelTracking?id=${trackingNumber}`;
  }
  // Otherwise use the provided tracking URL or fallback to Parcels App
  return trackingUrl || `https://parcelsapp.com/en/tracking/${trackingNumber}`;
};

const copyTracking = async (trackingNumber) => {
  await copyTextToClipboard(trackingNumber);
  useAlert(t('CONVERSATION_SIDEBAR.SHOPIFY.TRACKING_COPIED'));
};
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center">
      <div class="font-medium flex">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{ order.order_number || `#${order.id}` }}
          <i class="i-lucide-external-link pl-5" />
        </a>
      </div>
      <div
        :class="getStatusClass(order.financial_status)"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="financialStatus"
      >
        {{ financialStatus }}
      </div>
    </div>
    
    <!-- Fulfillment Status -->
    <div class="text-sm">
      <span
        :class="getFulfillmentClass(order.fulfillment_status)"
        class="font-medium"
      >
        {{ fulfillmentStatus }}
      </span>
    </div>

    <!-- Line Items -->
    <div v-if="hasLineItems" class="text-sm text-n-slate-11 space-y-0.5">
      <div v-for="item in order.line_items" :key="item.id" class="flex justify-between">
        <span class="truncate">{{ item.quantity }}x {{ item.name }}</span>
      </div>
    </div>

    <!-- Tracking Info -->
    <div v-if="hasTracking" class="text-sm space-y-1">
      <div v-for="(track, index) in order.tracking_info" :key="index" class="flex items-center gap-1 relative">
        <span class="text-n-slate-11">Track: </span>
        <a
          :href="getTrackingUrl(track.tracking_number, track.tracking_url)"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-teal-9 hover:underline"
        >
          {{ track.tracking_number }}
        </a>
        <!-- Copy button -->
        <button
          class="p-0.5 text-n-slate-11 hover:text-n-brand transition-colors"
          :title="t('CONVERSATION_SIDEBAR.SHOPIFY.COPY_TRACKING')"
          @click="copyTracking(track.tracking_number)"
        >
          <i class="i-lucide-copy text-xs" />
        </button>
        <!-- More tracking services dropdown -->
        <div class="relative">
          <button
            class="p-0.5 text-n-slate-11 hover:text-n-brand transition-colors"
            :title="t('CONVERSATION_SIDEBAR.SHOPIFY.MORE_TRACKING_SERVICES')"
            @click.stop="toggleTrackingDropdown(index)"
          >
            <i class="i-lucide-chevron-down text-xs" />
          </button>
          <!-- Dropdown menu -->
          <div
            v-if="openTrackingDropdown === index"
            v-on-clickaway="closeTrackingDropdown"
            class="absolute right-0 top-full mt-1 z-50 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg py-1 min-w-[160px]"
          >
            <div class="px-2 py-1 text-xs text-n-slate-11 border-b border-n-weak mb-1">
              {{ t('CONVERSATION_SIDEBAR.SHOPIFY.TRACK_WITH') }}
            </div>
            <a
              v-for="service in trackingServices"
              :key="service.id"
              :href="service.getUrl(track.tracking_number)"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="closeTrackingDropdown"
            >
              <i :class="service.icon" class="text-n-slate-11" />
              <span>{{ service.name }}</span>
              <span v-if="service.manualEntry" class="text-[10px] text-n-slate-11">(manual)</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Shipping Status -->
    <div class="text-sm">
      <span class="text-n-slate-11">Shipping: </span>
      <span :class="getShippingClass(shippingStatus)" class="font-medium">
        {{ shippingStatus }}
      </span>
    </div>

    <div class="text-sm text-n-slate-12">
      <span class="text-n-slate-11 border-r border-n-weak pr-2">
        {{ formatDate(order.created_at) }}
      </span>
      <span class="text-n-slate-11 pl-2">
        {{ formatCurrency(order.total_price, order.currency) }}
      </span>
    </div>
  </div>
</template>
